'use strict'
import BaseService from '../../services/baseService'
import GenusService from '../../services/genusService'
import domainUUIDByMGDB from '../../model/domain'
import SpeciesOfInterest from '../../model/speciesOfInterestModel'

function getCategories (distributorConfiguration) {
  return distributorConfiguration?.categories?.speciesOfInterestCategories || []
}

function getDefaultGroupedItems (
  groupItemsByCategory,
  primarySample,
  secondarySample
) {
  let defaultGroupedItems = groupItemsByCategory.default || []
  const genusService = new GenusService(primarySample, secondarySample)
  const defaultGroupSpeciesUUID = []
  defaultGroupedItems = defaultGroupedItems.map((item) => {
    if (item.isGenus) {
      item.genusDetails = genusService.getGenusDetailsByUUID(item.uuid)
      item.label =
        item?.genusDetails?.displayName ||
        `since it is undetected, not finding display name from glossary for (${item.uuid})???`
      item.speciesRelatedToCurrentGenus = genusService.getRelatedSpeciesDetailsForGivenGenus(
        item.uuid
      )

      const childSpeciesUUID = item.speciesRelatedToCurrentGenus.map(
        (item) => item.sampleSpeciesUUID
      )
      defaultGroupSpeciesUUID.push(...childSpeciesUUID)
    }
    if (item.isGenusGroup) {
      let speciesRelatedToCurrentGenusGroup = []
      item.containsGenera.forEach((item) => {
        const speciesRelatedToCurrentGenus = genusService.getRelatedSpeciesDetailsForGivenGenus(
          item.uuid
        )
        speciesRelatedToCurrentGenusGroup = [
          ...speciesRelatedToCurrentGenusGroup,
          ...speciesRelatedToCurrentGenus
        ]
      })
      item.speciesRelatedToCurrentGenusGroup = speciesRelatedToCurrentGenusGroup
      const childSpeciesUUID = item.speciesRelatedToCurrentGenusGroup.map(
        (item) => item.sampleSpeciesUUID
      )
      defaultGroupSpeciesUUID.push(...childSpeciesUUID)
    }

    if (item.isSpeciesGroup) {
      const speciesRelatedToCurrentSpeciesGroup = []
      item.containsSpecies.forEach((item) => {
        const speciesRelatedToCurrentSpecie = genusService.abundanceData.find(
          (specie) => specie.sampleSpeciesUUID === item.uuid
        )
        if (speciesRelatedToCurrentSpecie) {
          speciesRelatedToCurrentSpeciesGroup.push(
            speciesRelatedToCurrentSpecie
          )
        }
        defaultGroupSpeciesUUID.push(item.uuid)
      })
      item.speciesRelatedToCurrentSpeciesGroup = speciesRelatedToCurrentSpeciesGroup
    }

    return new SpeciesOfInterest(item)
  })

  return [defaultGroupedItems, defaultGroupSpeciesUUID]
}

function getOtherGroupedItems (
  defaultGroupSpeciesUUID,
  categoryKey,
  primarySample,
  secondarySample,
  MGDBVersion
) {
  if (!MGDBVersion) {
    throw new Error('Missing MGDB version.')
  }
  if (!categoryKey) {
    throw new Error('Missing category key.')
  }
  const domainUUID = domainUUIDByMGDB[MGDBVersion][categoryKey] || null
  if (!domainUUID) {
    throw new Error('Missing domain uuid.')
  }
  const genusService = new GenusService(primarySample, secondarySample)
  const allSpeciesForCurrentDomain =
    genusService.abundanceData?.filter(
      (specie) => specie.currentSpecieRelatedDomainUUID === domainUUID
    ) || null

  return allSpeciesForCurrentDomain?.filter(
    (item) => !defaultGroupSpeciesUUID.includes(item.sampleSpeciesUUID)
  ) || []
}

function getGenusSpeciesByCategory (
  category,
  groups,
  primarySample,
  secondarySample,
  MGDBVersion
) {
  const groupItemsByCategory = groups[category.key] || null
  const [defaultGroupedItems, defaultGroupSpeciesUUID] = groupItemsByCategory
    ? getDefaultGroupedItems(
      groupItemsByCategory,
      primarySample,
      secondarySample
    )
    : null

  const otherGroupedItems = groupItemsByCategory?.other
    ? getOtherGroupedItems(
      defaultGroupSpeciesUUID,
      category.key,
      primarySample,
      secondarySample,
      MGDBVersion
    )
    : null

  return {
    default: defaultGroupedItems,
    other: otherGroupedItems
      ? [
        new SpeciesOfInterest({
          isGenus: false,
          isGenusGroup: false,
          isSpeciesGroup: true,
          labelKey: 'Other',
          speciesRelatedToCurrentSpeciesGroup: otherGroupedItems
        })
      ]
      : null
  }
}

export default class DataService extends BaseService {
  constructor (
    distributorConfiguration,
    primarySampleData,
    secondarySampleData
  ) {
    if (!distributorConfiguration) {
      throw new Error('Distributor configuration is required.')
    }
    super(primarySampleData, secondarySampleData)
    this.distributorConfiguration = distributorConfiguration
  }

  getGenusSpeciesData () {
    const returnData = {}
    const grouping = this.distributorConfiguration?.grouping?.speciesOfInterest || {}
    const categories = getCategories(this.distributorConfiguration)
    categories.forEach((category) => {
      const genusSpeciesByCategory = getGenusSpeciesByCategory(
        category,
        grouping,
        this.primarySampleData,
        this.secondarySampleData,
        this.distributorConfiguration?.version
      )
      returnData[category.key] = genusSpeciesByCategory
    })
    return returnData
  }
}
